import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Server, Activity, ShieldAlert, ArrowRight, Plug } from 'lucide-react';
import { adminApi as api } from '@/lib/api';
import DashboardShell from '@/components/enterprise/DashboardShell';
import { Panel, StatusPill, Kpi } from '@/components/enterprise/ui';

const MODULES = [
  { key: 'proxmox', to: '/plattform/proxmox', label: 'Cloud / Proxmox', icon: Server, desc: 'Cluster, Nodes, VMs, Storage, Backups' },
  { key: 'monitoring', to: '/plattform/monitoring', label: 'Monitoring Center', icon: Activity, desc: 'KPIs, Charts, Alerts, Uptime' },
  { key: 'security', to: '/plattform/security', label: 'Security SOC', icon: ShieldAlert, desc: 'Threat Feed, SIEM, Incidents' },
];

export default function PlatformIndex() {
  const [health, setHealth] = useState({});
  const [integrations, setIntegrations] = useState([]);

  useEffect(() => {
    MODULES.forEach((m) => {
      api.get(`/dashboards/${m.key}`).then((r) => setHealth((h) => ({ ...h, [m.key]: r.data }))).catch(() => setHealth((h) => ({ ...h, [m.key]: { status: 'offline' } })));
    });
    api.get('/admin/integrations').then((r) => setIntegrations(r.data.integrations || [])).catch(() => {});
  }, []);

  return (
    <DashboardShell title="Operations Platform" subtitle="unified control plane · realtime">
      <div className="space-y-6">
        <div className="grid lg:grid-cols-3 gap-4">
          {MODULES.map((m) => {
            const h = health[m.key];
            const status = h ? (h.status === 'connected' ? 'connected' : h.status) : 'connecting';
            return (
              <Link key={m.key} to={m.to} data-testid={`platform-card-${m.key}`}
                className="group rounded-xl border border-[color:var(--itw-border)] bg-[color:var(--itw-surface)]/70 p-5 transition-[border-color,transform] duration-200 hover:border-[color:var(--itw-border-strong)] hover:-translate-y-0.5">
                <div className="flex items-center justify-between">
                  <span className="h-10 w-10 rounded-lg grid place-items-center border border-[color:var(--itw-border)] bg-[color:var(--itw-surface-2)]"><m.icon className="h-5 w-5 text-[color:var(--itw-cyan)]" /></span>
                  <StatusPill status={status === 'connecting' ? 'info' : status} label={status === 'connected' ? 'Live (mock)' : undefined} />
                </div>
                <h3 className="mt-4 text-base font-semibold text-[color:var(--itw-text)]">{m.label}</h3>
                <p className="mt-1 text-sm text-[color:var(--itw-text-2)]">{m.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-[color:var(--itw-cyan)]">Öffnen <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
              </Link>
            );
          })}
        </div>

        <Panel title="Integrationen" subtitle="connector registry · mock → live austauschbar" testid="platform-integrations">
          <div className="space-y-2">
            {integrations.length === 0 && <div className="text-sm text-[color:var(--itw-text-3)] font-mono">loading…</div>}
            {integrations.map((it) => (
              <div key={it.type} className="flex items-center justify-between rounded-lg border border-[color:var(--itw-border)] px-3 py-2.5">
                <span className="flex items-center gap-2.5"><Plug className="h-4 w-4 text-[color:var(--itw-text-3)]" /><span className="text-sm text-[color:var(--itw-text)]">{it.name}</span><span className="text-[11px] font-mono text-[color:var(--itw-text-3)]">{it.category}</span></span>
                <span className="flex items-center gap-2"><span className="text-[11px] font-mono uppercase text-[color:var(--itw-text-3)]">{it.mode}</span><StatusPill status={it.enabled ? 'connected' : 'offline'} label={it.enabled ? 'Ready' : 'Disabled'} /></span>
              </div>
            ))}
          </div>
        </Panel>

        <p className="text-[11px] text-[color:var(--itw-text-3)] font-mono">Hinweis: Aktuell Mock-Provider. Architektur (Connector → Provider → API) ist vorbereitet – beim Aktivieren echter Integrationen wird nur die Datenquelle getauscht, ohne UI-Änderung.</p>
      </div>
    </DashboardShell>
  );
}
